import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AlertTriangle } from 'lucide-react';
import RiskDisclaimerBanner from '../components/RiskDisclaimerBanner';

function useCounter(target, duration = 2000, started = true) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!started) return;
    let id;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      setVal(+(target * (1 - Math.pow(1 - p, 3))).toFixed(2));
      if (p < 1) id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [target, duration, started]);
  return val;
}

function useVisible(thresh = 0.1) {
  const [v, setV] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setV(true),
      { threshold: thresh }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, v];
}

const BAR_DATA = [
  { m: 'Mar', v:  2.34, yr: '2023' }, { m: 'Apr', v: 23.84, yr: '2023' },
  { m: 'May', v: 16.44, yr: '2023' }, { m: 'Jun', v:  3.55, yr: '2023' },
  { m: 'Jul', v:  5.85, yr: '2023' }, { m: 'Aug', v: -2.99, yr: '2023' },
  { m: 'Sep', v: 19.52, yr: '2023' }, { m: 'Oct', v: 10.48, yr: '2023' },
  { m: 'Jan', v: 10.15, yr: '2024' }, { m: 'Feb', v: -5.07, yr: '2024' },
  { m: 'Mar', v:  3.95, yr: '2024' }, { m: 'Apr', v: 38.53, yr: '2024' },
  { m: 'May', v: 12.21, yr: '2024' }, { m: 'Jun', v:  6.67, yr: '2024' },
  { m: 'Jul', v: 13.99, yr: '2024' }, { m: 'Aug', v: 15.69, yr: '2024' },
  { m: 'Sep', v: -6.84, yr: '2024' }, { m: 'Oct', v:  6.97, yr: '2024' },
  { m: 'Jan', v: 41.08, yr: '2025' }, { m: 'Feb', v: 32.92, yr: '2025' },
  { m: 'Mar', v: 19.62, yr: '2025' }, { m: 'Apr', v: 25.22, yr: '2025' },
  { m: 'May', v: 52.79, yr: '2025' }, { m: 'Jun', v: 42.52, yr: '2025' },
  { m: 'Jul', v: -6.64, yr: '2025' },
];

const MONTHLY_GRID = [
  { year: 2023, months: [null, null, 2.34, 23.84, 16.44, 3.55, 5.85, -2.99, 19.52, 10.48, null, null] },
  { year: 2024, months: [10.15, -5.07, 3.95, 38.53, 12.21, 6.67, 13.99, 15.69, -6.84, 6.97, null, null] },
  { year: 2025, months: [41.08, 32.92, 19.62, 25.22, 52.79, 42.52, -6.64, null, null, null, null, null] },
];
const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const AUDIT_ROWS = [
  { label: 'Net Profit',      all: '$339,934',   long: '-$92,910', short: '$432,845'   },
  { label: 'Gross Profit',    all: '$1,878,623', long: '$726,405', short: '$1,152,218' },
  { label: 'Gross Loss',      all: '$1,538,689', long: '$819,316', short: '$719,372'   },
  { label: 'Commission',      all: '$101,444',   long: '$44,330',  short: '$57,113'    },
  { label: 'Buy & Hold Ret.', all: '-$54,234',   long: '—',        short: '—'          },
  { label: 'Max Runup',       all: '$375,809',   long: '—',        short: '—'          },
  { label: 'Max Drawdown',    all: '$89,181',    long: '—',        short: '—'          },
];

const TRADE_STATS = [
  { label: 'Total Trades',   value: '779',        color: '#e0e0e0' },
  { label: 'Win Rate',       value: '56.48%',     color: '#38FE60', note: '440 winning trades' },
  { label: 'Profit Factor',  value: '1.22×',      color: '#38FE60', note: 'wins outpace losses' },
  { label: 'Average Win',    value: '$4,269',     color: '#38FE60' },
  { label: 'Largest Win',    value: '$126,534',   color: '#38FE60' },
  { label: 'Net Profit',     value: '$339,639',   color: '#38FE60' },
  { label: 'Avg Per Trade',  value: '+$437',      color: '#38FE60' },
  { label: 'Total Return',   value: '+3,919%',    color: '#38FE60' },
];

const EQUITY_PTS = (() => {
  let v = 100;
  const pts = [{ label: 'Inception', pct: 0, portfolio: 10000 }];
  BAR_DATA.forEach(d => {
    v *= (1 + d.v / 100);
    pts.push({ label: `${d.m} ${d.yr}`, pct: +(v - 100).toFixed(2), portfolio: Math.round(v * 100) });
  });
  return pts;
})();

const CARD = {
  bg: 'rgba(255,255,255,0.025)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderHov: '1px solid rgba(56,254,96,0.22)',
  radius: 22,
};

const RiskWarningModal = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(true); }, []);
  const dismiss = () => setOpen(false);
  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.88)',
      backdropFilter: 'blur(16px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <div style={{
        background: '#18181a',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 28,
        maxWidth: 460,
        width: '100%',
        padding: '44px 40px 36px',
        boxShadow: '0 48px 96px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)',
        animation: 'modalIn 0.35s cubic-bezier(0.34,1.56,0.64,1)',
      }}>
        <style>{`@keyframes modalIn { from { opacity:0; transform:scale(0.92) translateY(16px) } to { opacity:1; transform:none } }`}</style>
        <div style={{
          width: 52, height: 52, borderRadius: 16,
          background: 'rgba(251,191,36,0.1)',
          border: '1px solid rgba(251,191,36,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 28,
        }}>
          <AlertTriangle style={{ color: '#fbbf24', width: 22, height: 22 }} />
        </div>
        <h2 style={{ color: '#fff', fontSize: 19, fontWeight: 700, marginBottom: 10, letterSpacing: '-0.03em' }}>
          Important Risk Disclosure
        </h2>
        <p style={{ color: '#777', fontSize: 13, lineHeight: 1.75, marginBottom: 10 }}>
          All results shown are based on <span style={{ color: '#aaa', fontWeight: 600 }}>simulated backtesting</span> using historical market data. Past performance is not indicative of future results.
        </p>
        <p style={{ color: '#777', fontSize: 13, lineHeight: 1.75, marginBottom: 36 }}>
          Trading involves a substantial risk of loss. Only risk capital you can afford to lose. These figures may not reflect real trading conditions, slippage, or execution costs.
        </p>
        <button onClick={dismiss} style={{
          width: '100%', padding: '15px',
          background: '#38FE60', color: '#000',
          border: 'none', borderRadius: 14,
          fontSize: 14, fontWeight: 700,
          cursor: 'pointer', letterSpacing: '0.01em',
          fontFamily: "'Space Grotesk', sans-serif",
          boxShadow: '0 0 28px rgba(56,254,96,0.35)',
          transition: 'box-shadow 0.2s, transform 0.15s',
        }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 44px rgba(56,254,96,0.55)'; e.currentTarget.style.transform = 'scale(1.015)'; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 0 28px rgba(56,254,96,0.35)'; e.currentTarget.style.transform = 'scale(1)'; }}
        >
          I Understand — View Results
        </button>
        <p style={{ color: '#333', fontSize: 11, textAlign: 'center', marginTop: 14 }}>
          This notice is shown once per session
        </p>
      </div>
    </div>
  );
};

const EquityChart = () => {
  const canvasRef    = useRef(null);
  const containerRef = useRef(null);
  const hoverIdxRef  = useRef(null);
  const ptsRef       = useRef([]);
  const [hoverIdx, setHoverIdx] = useState(null);

  const drawCanvas = useCallback((hIdx) => {
    const canvas    = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const W   = container.clientWidth;
    const H   = container.clientHeight;

    canvas.width  = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width  = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    const pL = 56, pR = 16, pT = 16, pB = 30;
    const cW = W - pL - pR;
    const cH = H - pT - pB;

    const vals  = EQUITY_PTS.map(p => p.portfolio);
    const maxV  = Math.max(...vals) * 1.06;
    const minV  = 7000;

    const toX = i => pL + (i / (EQUITY_PTS.length - 1)) * cW;
    const toY = v => pT + (1 - (v - minV) / (maxV - minV)) * cH;

    const pts = EQUITY_PTS.map((p, i) => ({ x: toX(i), y: toY(p.portfolio) }));
    ptsRef.current = pts;

    
    ctx.font = `10px 'Space Grotesk', monospace`;
    [10000, 50000, 150000, 300000].forEach(v => {
      if (v > maxV) return;
      const y = toY(v);
      ctx.strokeStyle = 'rgba(255,255,255,0.045)';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(pL, y); ctx.lineTo(pL + cW, y); ctx.stroke();
      ctx.fillStyle = 'rgba(255,255,255,0.22)';
      ctx.textAlign = 'right';
      ctx.fillText(v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`, pL - 8, y + 3.5);
    });

    
    [{ label: '2023', idx: 0 }, { label: '2024', idx: 8 }, { label: '2025', idx: 18 }].forEach(({ label, idx }) => {
      const x = toX(idx);
      ctx.strokeStyle = 'rgba(255,255,255,0.06)';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 5]);
      ctx.beginPath(); ctx.moveTo(x, pT); ctx.lineTo(x, pT + cH); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = 'rgba(255,255,255,0.22)';
      ctx.textAlign = 'center';
      ctx.fillText(label, x, H - 8);
    });

    
    const spline = (path, points) => {
      path.moveTo(points[0].x, points[0].y);
      for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[Math.max(i - 1, 0)];
        const p1 = points[i], p2 = points[i + 1];
        const p3 = points[Math.min(i + 2, points.length - 1)];
        path.bezierCurveTo(
          p1.x + (p2.x - p0.x) / 6, p1.y + (p2.y - p0.y) / 6,
          p2.x - (p3.x - p1.x) / 6, p2.y - (p3.y - p1.y) / 6,
          p2.x, p2.y,
        );
      }
    };

    
    const fill = new Path2D();
    spline(fill, pts);
    fill.lineTo(pts[pts.length - 1].x, pT + cH);
    fill.lineTo(pts[0].x, pT + cH);
    fill.closePath();
    const grad = ctx.createLinearGradient(0, pT, 0, pT + cH);
    grad.addColorStop(0,   'rgba(56,254,96,0.16)');
    grad.addColorStop(0.5, 'rgba(56,254,96,0.05)');
    grad.addColorStop(1,   'rgba(56,254,96,0.00)');
    ctx.fillStyle = grad;
    ctx.fill(fill);

    
    const line = new Path2D();
    spline(line, pts);
    ctx.strokeStyle = '#38FE60';
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.shadowColor = 'rgba(56,254,96,0.5)';
    ctx.shadowBlur  = 10;
    ctx.stroke(line);
    ctx.shadowBlur  = 0;

    
    if (hIdx !== null && hIdx >= 0 && hIdx < pts.length) {
      const pt = pts[hIdx];

      ctx.strokeStyle = 'rgba(255,255,255,0.11)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(pt.x, pT); ctx.lineTo(pt.x, pT + cH); ctx.stroke();
      ctx.setLineDash([3, 6]);
      ctx.beginPath(); ctx.moveTo(pL, pt.y); ctx.lineTo(pL + cW, pt.y); ctx.stroke();
      ctx.setLineDash([]);

      ctx.beginPath();
      ctx.arc(pt.x, pt.y, 8, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(56,254,96,0.18)';
      ctx.fill();

      ctx.beginPath();
      ctx.arc(pt.x, pt.y, 4, 0, Math.PI * 2);
      ctx.fillStyle = '#38FE60';
      ctx.shadowColor = 'rgba(56,254,96,1)';
      ctx.shadowBlur  = 16;
      ctx.fill();
      ctx.shadowBlur  = 0;
    }
  }, []);

  useEffect(() => { drawCanvas(hoverIdx); }, [hoverIdx, drawCanvas]);

  useEffect(() => {
    drawCanvas(null);
    const ro = new ResizeObserver(() => drawCanvas(hoverIdxRef.current));
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [drawCanvas]);

  const handleMouseMove = e => {
    const canvas = canvasRef.current;
    if (!canvas || !ptsRef.current.length) return;
    const rect = canvas.getBoundingClientRect();
    const mx   = e.clientX - rect.left;
    let closest = 0, minDist = Infinity;
    ptsRef.current.forEach((p, i) => {
      const d = Math.abs(p.x - mx);
      if (d < minDist) { minDist = d; closest = i; }
    });
    hoverIdxRef.current = closest;
    setHoverIdx(closest);
  };

  const hovered  = hoverIdx !== null ? EQUITY_PTS[hoverIdx]     : null;
  const hovPt    = hoverIdx !== null ? ptsRef.current[hoverIdx]  : null;
  const ctrWidth = containerRef.current?.clientWidth || 400;

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', height: '100%' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => { hoverIdxRef.current = null; setHoverIdx(null); }}>
      <canvas ref={canvasRef} style={{ display: 'block', cursor: 'crosshair' }} />
      {hovered && hovPt && (
        <div style={{
          position: 'absolute', top: 12, pointerEvents: 'none', zIndex: 5,
          left: Math.min(hovPt.x + 16, ctrWidth - 156),
          background: 'rgba(15,15,17,0.94)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 12, padding: '11px 16px',
          backdropFilter: 'blur(12px)', minWidth: 148,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        }}>
          <p style={{ color: '#555', fontSize: 10, marginBottom: 6, letterSpacing: '0.07em', textTransform: 'uppercase' }}>
            {hovered.label}
          </p>
          <p style={{ color: '#38FE60', fontSize: 20, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 3, lineHeight: 1 }}>
            {hovered.pct >= 0 ? '+' : ''}{hovered.pct.toFixed(1)}%
          </p>
          <p style={{ color: '#666', fontSize: 12 }}>
            ${hovered.portfolio.toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
};

const HeroCard = () => {
  const [ref, vis] = useVisible(0.05);
  const ret = useCounter(3919.8, 2600, vis);

  return (
    <div ref={ref} style={{
      borderRadius: CARD.radius,
      border: CARD.border,
      background: CARD.bg,
      display: 'flex',
      flexWrap: 'wrap',
      minHeight: 300,
      marginBottom: 10,
      overflow: 'hidden',
      opacity: vis ? 1 : 0,
      transform: vis ? 'none' : 'translateY(24px)',
      transition: 'opacity 0.9s ease, transform 0.9s ease',
    }}>
            <div style={{
        flex: '0 0 280px',
        padding: '40px 40px',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        borderRight: '1px solid rgba(255,255,255,0.05)',
        background: 'linear-gradient(145deg, rgba(56,254,96,0.04) 0%, transparent 55%)',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 18 }}>
            <span style={{
              display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
              background: '#38FE60', boxShadow: '0 0 10px #38FE60',
            }} />
            <span style={{ color: '#38FE60', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600 }}>
              Simulated Return
            </span>
          </div>
          <div style={{
            fontSize: 'clamp(3rem, 7.5vw, 5rem)',
            fontWeight: 800, color: '#fff',
            letterSpacing: '-0.05em', lineHeight: 1,
            marginBottom: 14,
          }}>
            +{ret.toFixed(1)}%
          </div>
          <p style={{ color: '#555', fontSize: 13, lineHeight: 1.7 }}>
            Starting from <span style={{ color: '#888', fontWeight: 600 }}>$10,000</span><br />
            Mar 2023 → Jul 2025
          </p>
        </div>

        <div style={{ display: 'flex', paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.05)', gap: 0 }}>
          {[['779', 'Trades'], ['25', 'Months'], ['$10K', 'Capital']].map(([v, l], i) => (
            <div key={l} style={{
              flex: 1,
              paddingRight: i < 2 ? 16 : 0,
              marginRight: i < 2 ? 16 : 0,
              borderRight: i < 2 ? '1px solid rgba(255,255,255,0.05)' : 'none',
            }}>
              <p style={{ color: '#fff', fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em' }}>{v}</p>
              <p style={{ color: '#444', fontSize: 11, marginTop: 3 }}>{l}</p>
            </div>
          ))}
        </div>
      </div>

            <div style={{ flex: 1, minWidth: 260, padding: '20px 20px 8px 20px', minHeight: 260 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ color: '#444', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Portfolio Growth
          </span>
          <span style={{ color: '#333', fontSize: 11, fontFamily: 'monospace' }}>
            $10K → $391,980
          </span>
        </div>
        <div style={{ height: 'calc(100% - 28px)', minHeight: 200 }}>
          <EquityChart />
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({ label, value, color, sub, delay = 0 }) => {
  const [ref, vis] = useVisible(0.1);
  const [hov, setHov] = useState(false);
  return (
    <div ref={ref}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        borderRadius: CARD.radius,
        padding: '28px 28px 24px',
        background: CARD.bg,
        border: hov ? CARD.borderHov : CARD.border,
        boxShadow: hov ? '0 16px 48px rgba(0,0,0,0.35)' : 'none',
        transition: `border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease, opacity 0.65s ${delay}ms ease`,
        transform: vis ? (hov ? 'translateY(-3px)' : 'none') : 'translateY(18px)',
        opacity: vis ? 1 : 0,
        cursor: 'default',
      }}>
      <p style={{
        fontSize: 'clamp(1.7rem, 3vw, 2.25rem)',
        fontWeight: 800, color,
        letterSpacing: '-0.04em', lineHeight: 1,
        marginBottom: 10,
      }}>
        {value}
      </p>
      <p style={{ color: '#888', fontSize: 12, fontWeight: 500, marginBottom: sub ? 4 : 0 }}>{label}</p>
      {sub && <p style={{ color: '#3e3e3e', fontSize: 11 }}>{sub}</p>}
    </div>
  );
};

const cellColor = v => {
  if (v === null) return { bg: 'transparent',               fg: 'rgba(255,255,255,0.12)', fw: 400 };
  if (v >= 40)   return { bg: 'rgba(56,254,96,0.22)',       fg: '#38FE60',                fw: 700 };
  if (v >= 20)   return { bg: 'rgba(56,254,96,0.13)',       fg: '#6aff9e',                fw: 700 };
  if (v >= 10)   return { bg: 'rgba(56,254,96,0.08)',       fg: '#9affb8',                fw: 600 };
  if (v >= 0)    return { bg: 'rgba(56,254,96,0.04)',       fg: '#888',                   fw: 500 };
  return               { bg: 'rgba(255,69,58,0.13)',        fg: '#ff6b63',                fw: 600 };
};

const MonthlyGrid = () => {
  const [ref, vis] = useVisible(0.1);
  const [hov, setHov] = useState(null);

  return (
    <div ref={ref} style={{
      borderRadius: CARD.radius, overflow: 'hidden',
      border: CARD.border, background: CARD.bg,
      marginBottom: 10,
      opacity: vis ? 1 : 0, transform: vis ? 'none' : 'translateY(20px)',
      transition: 'opacity 0.7s 0.1s ease, transform 0.7s 0.1s ease',
    }}>
      <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <h3 style={{ color: '#fff', fontSize: 15, fontWeight: 700, letterSpacing: '-0.02em' }}>
          Monthly Returns
        </h3>
        <p style={{ color: '#444', fontSize: 12, marginTop: 4 }}>
          Color intensity reflects performance magnitude
        </p>
      </div>

      <div style={{ overflowX: 'auto', padding: '16px 20px 20px' }}>
        <table style={{ width: '100%', minWidth: 620, borderCollapse: 'separate', borderSpacing: '3px 3px' }}>
          <thead>
            <tr>
              <th style={{ width: 52, textAlign: 'left', paddingBottom: 8, paddingLeft: 6 }}>
                <span style={{ color: '#333', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Year</span>
              </th>
              {MONTH_LABELS.map(m => (
                <th key={m} style={{ textAlign: 'center', paddingBottom: 8, minWidth: 50 }}>
                  <span style={{ color: '#333', fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{m}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MONTHLY_GRID.map((row, ri) => (
              <tr key={row.year}>
                <td style={{ paddingLeft: 6 }}>
                  <span style={{ color: '#555', fontSize: 12, fontWeight: 600 }}>{row.year}</span>
                </td>
                {row.months.map((v, ci) => {
                  const s = cellColor(v);
                  const active = hov?.ri === ri && hov?.ci === ci;
                  return (
                    <td key={ci}
                      onMouseEnter={() => v !== null && setHov({ ri, ci })}
                      onMouseLeave={() => setHov(null)}
                      style={{
                        textAlign: 'center',
                        padding: '9px 4px',
                        borderRadius: 8,
                        background: active
                          ? (v >= 0 ? 'rgba(56,254,96,0.32)' : 'rgba(255,69,58,0.28)')
                          : s.bg,
                        transition: 'background 0.18s ease',
                        cursor: v !== null ? 'default' : 'default',
                      }}>
                      <span style={{
                        fontSize: 11, fontFamily: 'monospace',
                        fontWeight: s.fw,
                        color: active && v !== null
                          ? (v >= 0 ? '#38FE60' : '#ff453a')
                          : s.fg,
                        letterSpacing: '-0.01em',
                      }}>
                        {v === null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TradeStats = () => {
  const [ref, vis] = useVisible(0.1);
  return (
    <div ref={ref} style={{
      opacity: vis ? 1 : 0, transform: vis ? 'none' : 'translateY(16px)',
      transition: 'opacity 0.6s ease, transform 0.6s ease',
    }}>
      <div style={{ borderRadius: CARD.radius, overflow: 'hidden', border: CARD.border, background: CARD.bg }}>
        <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <h3 style={{ color: '#fff', fontSize: 15, fontWeight: 700, letterSpacing: '-0.02em' }}>Trade Statistics</h3>
          <p style={{ color: '#444', fontSize: 12, marginTop: 4 }}>779 trades — Mar 2023 to Jul 2025</p>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 1,
          background: 'rgba(255,255,255,0.04)',
        }}>
          {TRADE_STATS.map(s => (
            <div key={s.label} style={{ padding: '22px 26px', background: '#111113' }}>
              <p style={{ color: s.color, fontSize: 22, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 6, lineHeight: 1 }}>
                {s.value}
              </p>
              <p style={{ color: '#555', fontSize: 11 }}>{s.label}</p>
              {s.note && (
                <p style={{ color: '#555', fontSize: 10, marginTop: 3 }}>{s.note}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AuditRow = ({ row, i }) => {
  const [hov, setHov] = useState(false);
  const valColor = v => {
    if (v === '—') return '#333';
    if (v.startsWith('-')) return '#ff6b63';
    if (v.startsWith('$')) return '#e8e8e8';
    return '#666';
  };
  const isPositive  = v => v.startsWith('$') && !v.startsWith('-');
  const isNegative  = v => v.startsWith('-');

  return (
    <tr
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        background: hov ? 'rgba(255,255,255,0.03)' : i % 2 !== 0 ? 'rgba(255,255,255,0.01)' : 'transparent',
        transition: 'background 0.15s ease',
      }}>
            <td style={{ padding: '16px 28px 16px 24px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{
          width: 3, height: 20, borderRadius: 2, flexShrink: 0,
          background: isNegative(row.all) ? '#ff6b63' : 'rgba(56,254,96,0.5)',
          display: 'inline-block',
        }} />
        <span style={{ color: hov ? '#ccc' : '#777', fontSize: 13, fontWeight: 500, transition: 'color 0.15s' }}>{row.label}</span>
      </td>
      {[row.all, row.long, row.short].map((v, j) => (
        <td key={j} style={{
          padding: '16px 28px', textAlign: 'right', verticalAlign: 'middle',
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6,
          }}>
            {v !== '—' && (
              <span style={{
                width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
                background: isNegative(v) ? '#ff6b63' : isPositive(v) ? 'rgba(56,254,96,0.6)' : 'transparent',
              }} />
            )}
            <span style={{
              color: valColor(v),
              fontSize: 13, fontWeight: 700,
              fontFamily: 'monospace', letterSpacing: '-0.01em',
              textShadow: isNegative(v) ? '0 0 12px rgba(255,107,99,0.2)' : isPositive(v) && hov ? '0 0 12px rgba(56,254,96,0.15)' : 'none',
            }}>
              {v}
            </span>
          </span>
        </td>
      ))}
    </tr>
  );
};

const AuditTable = () => {
  const [ref, vis] = useVisible(0.1);
  return (
    <div ref={ref} style={{
      borderRadius: CARD.radius, overflow: 'hidden',
      border: CARD.border, background: CARD.bg,
      opacity: vis ? 1 : 0, transform: vis ? 'none' : 'translateY(16px)',
      transition: 'opacity 0.6s ease, transform 0.6s ease',
    }}>
            <div style={{
        padding: '24px 28px 20px',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
        background: 'linear-gradient(135deg, rgba(56,254,96,0.02) 0%, transparent 50%)',
      }}>
        <div>
          <h3 style={{ color: '#fff', fontSize: 15, fontWeight: 700, letterSpacing: '-0.02em' }}>Strategy Audit</h3>
          <p style={{ color: '#444', fontSize: 12, marginTop: 4 }}>All / Long / Short position breakdown</p>
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          {[['All', '#e8e8e8'], ['Long', '#38bdf8'], ['Short', '#a78bfa']].map(([l, c]) => (
            <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: c, display: 'inline-block' }} />
              <span style={{ color: '#444', fontSize: 11 }}>{l}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 520, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              {[
                { label: 'Metric',        align: 'left',  pl: '24px' },
                { label: 'All Positions', align: 'right', pl: '28px' },
                { label: 'Long',          align: 'right', pl: '28px' },
                { label: 'Short',         align: 'right', pl: '28px' },
              ].map(h => (
                <th key={h.label} style={{
                  textAlign: h.align, paddingLeft: h.pl,
                  color: '#2e2e2e', fontSize: 10, fontWeight: 700,
                  letterSpacing: '0.1em', textTransform: 'uppercase',
                  padding: '14px 28px',
                  background: 'rgba(255,255,255,0.01)',
                }}>
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {AUDIT_ROWS.map((row, i) => (
              <AuditRow key={row.label} row={row} i={i} />
            ))}
          </tbody>
        </table>
      </div>

            <div style={{
        padding: '12px 28px',
        borderTop: '1px solid rgba(255,255,255,0.04)',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#333', flexShrink: 0 }} />
        <p style={{ color: '#2e2e2e', fontSize: 11 }}>
          Figures are gross before commissions. Commission total: <span style={{ color: '#444' }}>$101,444</span>
        </p>
      </div>
    </div>
  );
};

const AI_INSIGHTS = [
  { label: 'Strategy Health',   value: '94 / 100', color: '#38FE60', note: 'Excellent consistency'    },
  { label: 'Trend Alignment',   value: '87%',       color: '#38FE60', note: 'Bull market edge'         },
  { label: 'Drawdown Control',  value: 'Strong',    color: '#38bdf8', note: 'Below 25% max'            },
  { label: 'Volatility Adapt.', value: 'High',      color: '#a78bfa', note: 'Adapts to regime changes' },
];

const AIAnalysis = () => {
  const [ref, vis] = useVisible(0.1);
  return (
    <div ref={ref} style={{
      marginTop: 32,
      opacity: vis ? 1 : 0, transform: vis ? 'none' : 'translateY(20px)',
      transition: 'opacity 0.7s ease, transform 0.7s ease',
    }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <span style={{ color: '#38FE60', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600 }}>
          AI-Powered
        </span>
      </div>
      <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 800, letterSpacing: '-0.04em', marginBottom: 6 }}>
        Performance AI Analysis
      </h2>
      <p style={{ color: '#555', fontSize: 13, maxWidth: 520, lineHeight: 1.75, marginBottom: 24 }}>
        Our AI engine continuously analyses strategy behaviour, regime alignment, and risk-adjusted metrics to surface actionable insights.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 10 }}>
        {AI_INSIGHTS.map((item, i) => (
          <AIInsightCard key={item.label} item={item} delay={i * 60} />
        ))}
      </div>

            <div style={{
        marginTop: 10,
        padding: '18px 24px',
        borderRadius: 16,
        background: 'rgba(56,254,96,0.04)',
        border: '1px solid rgba(56,254,96,0.1)',
        display: 'flex', alignItems: 'flex-start', gap: 14,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 10, flexShrink: 0,
          background: 'rgba(56,254,96,0.12)', border: '1px solid rgba(56,254,96,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#38FE60" strokeWidth="2" strokeLinecap="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
        </div>
        <div>
          <p style={{ color: '#38FE60', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>AI Summary</p>
          <p style={{ color: '#666', fontSize: 12, lineHeight: 1.7 }}>
            Strategy demonstrates <span style={{ color: '#aaa' }}>above-average consistency</span> with strong trend alignment and controlled drawdowns. Performance accelerates significantly in high-momentum regimes (2024–2025), suggesting a trend-following edge with adaptive position sizing.
          </p>
        </div>
      </div>
    </div>
  );
};

const AIInsightCard = ({ item, delay }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: '22px 24px',
        borderRadius: CARD.radius,
        background: CARD.bg,
        border: hov ? CARD.borderHov : CARD.border,
        boxShadow: hov ? '0 12px 36px rgba(0,0,0,0.3)' : 'none',
        transform: hov ? 'translateY(-2px)' : 'none',
        transition: `all 0.25s ease ${delay}ms`,
        cursor: 'default',
      }}>
      <p style={{ color: item.color, fontSize: 22, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 6, lineHeight: 1 }}>
        {item.value}
      </p>
      <p style={{ color: '#888', fontSize: 12, marginBottom: 4 }}>{item.label}</p>
      <p style={{ color: '#3a3a3a', fontSize: 11 }}>{item.note}</p>
    </div>
  );
};

const DAILY_BREAKDOWN = [
  { label: 'Avg Trades / Day',       value: '1.44',   color: '#38FE60' },
  { label: 'Total Trades',           value: '779',    color: '#e0e0e0' },
  { label: 'Active Trading Days',    value: '541',    color: '#e0e0e0' },
  { label: 'Best Single Day',        value: '+38.5%', color: '#38FE60' },
  { label: 'Worst Single Day',       value: '−6.84%', color: '#ff6b63' },
  { label: 'Avg Hold Time',          value: '8.6 bars', color: '#a78bfa' },
];

const TradesPerDay = () => {
  const [ref, vis] = useVisible(0.1);
  return (
    <div ref={ref} style={{
      marginTop: 40,
      opacity: vis ? 1 : 0, transform: vis ? 'none' : 'translateY(20px)',
      transition: 'opacity 0.7s 0.05s ease, transform 0.7s 0.05s ease',
    }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 800, letterSpacing: '-0.04em', marginBottom: 6 }}>
          Trades per Day
        </h2>
        <p style={{ color: '#555', fontSize: 13, lineHeight: 1.7 }}>
          Execution cadence and daily activity metrics across the full backtest period.
        </p>
      </div>

      <div style={{ borderRadius: CARD.radius, overflow: 'hidden', border: CARD.border, background: CARD.bg }}>
                <div style={{
          padding: '32px 32px 24px',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 32,
          background: 'linear-gradient(135deg, rgba(56,254,96,0.04) 0%, transparent 50%)',
        }}>
          <div>
            <p style={{ color: '#38FE60', fontSize: 56, fontWeight: 800, letterSpacing: '-0.05em', lineHeight: 1 }}>1.44</p>
            <p style={{ color: '#666', fontSize: 13, marginTop: 8 }}>Average trades executed per trading day</p>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <p style={{ color: '#555', fontSize: 12, lineHeight: 1.75 }}>
              Across <span style={{ color: '#888' }}>541 active trading days</span>, the strategy averaged 1.44 entries. Peak execution occurred in 2025 with significantly higher conviction setups driven by trend acceleration.
            </p>
          </div>
        </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, background: 'rgba(255,255,255,0.04)' }}>
          {DAILY_BREAKDOWN.map(s => (
            <div key={s.label} style={{ padding: '20px 24px', background: '#111113' }}>
              <p style={{ color: s.color, fontSize: 20, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 6, lineHeight: 1 }}>{s.value}</p>
              <p style={{ color: '#555', fontSize: 11 }}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const BT_FEATURES = [
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
    ),
    color: '#38FE60',
    title: 'Real-Time Signal Replay',
    desc: 'Every entry and exit is replayed tick-by-tick using the exact same logic deployed live — no look-ahead bias.',
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
    ),
    color: '#a78bfa',
    title: 'Multi-Asset Portfolio Testing',
    desc: 'Run simultaneous backtests across hundreds of crypto pairs. Evaluate correlation and portfolio-level drawdowns.',
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
    ),
    color: '#38bdf8',
    title: 'Slippage & Commission Models',
    desc: 'Realistic execution simulation including exchange fees, funding rates, and configurable slippage estimates.',
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
    ),
    color: '#fbbf24',
    title: 'Regime & Volatility Analysis',
    desc: 'Segment results by market regime — bull, bear, sideways — to understand where the strategy truly performs.',
  },
];

const BacktestingFeatures = () => {
  const [ref, vis] = useVisible(0.1);
  return (
    <div ref={ref} style={{
      marginTop: 52,
      opacity: vis ? 1 : 0, transform: vis ? 'none' : 'translateY(20px)',
      transition: 'opacity 0.7s ease, transform 0.7s ease',
    }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ color: '#38FE60', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600 }}>
            Infrastructure
          </span>
        </div>
        <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 800, letterSpacing: '-0.04em', marginBottom: 6 }}>
          Powerful Backtesting Features
        </h2>
        <p style={{ color: '#555', fontSize: 13, maxWidth: 500, lineHeight: 1.75 }}>
          Built on institutional-grade infrastructure. Every result is reproducible, auditable, and free from data snooping.
        </p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
        {BT_FEATURES.map((f, i) => (
          <FeatureCard key={f.title} feature={f} delay={i * 60} />
        ))}
      </div>
    </div>
  );
};

const FeatureCard = ({ feature: f, delay }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: '26px 26px',
        borderRadius: CARD.radius,
        background: CARD.bg,
        border: hov ? CARD.borderHov : CARD.border,
        boxShadow: hov ? '0 16px 48px rgba(0,0,0,0.35)' : 'none',
        transform: hov ? 'translateY(-3px)' : 'none',
        transition: `all 0.25s ease`,
        cursor: 'default',
        transitionDelay: `${delay}ms`,
      }}>
      <div style={{
        width: 40, height: 40, borderRadius: 12, marginBottom: 18,
        background: `rgba(${f.color === '#38FE60' ? '56,254,96' : f.color === '#a78bfa' ? '167,139,250' : f.color === '#38bdf8' ? '56,189,248' : '251,191,36'},0.1)`,
        border: `1px solid rgba(${f.color === '#38FE60' ? '56,254,96' : f.color === '#a78bfa' ? '167,139,250' : f.color === '#38bdf8' ? '56,189,248' : '251,191,36'},0.2)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: f.color,
      }}>
        {f.icon}
      </div>
      <p style={{ color: '#e0e0e0', fontSize: 14, fontWeight: 700, marginBottom: 8, letterSpacing: '-0.02em' }}>{f.title}</p>
      <p style={{ color: '#555', fontSize: 12, lineHeight: 1.7 }}>{f.desc}</p>
    </div>
  );
};

const HOW_STEPS = [
  {
    step: '01',
    title: 'Historical Data Ingestion',
    desc: 'We pull OHLCV data from verified exchange sources, cleaning for splits, delistings, and data gaps to ensure a pristine test environment.',
    color: '#38FE60',
  },
  {
    step: '02',
    title: 'Strategy Logic Execution',
    desc: 'The exact same live strategy logic — signals, filters, sizing — is applied bar-by-bar with no future data leakage and with realistic order fills.',
    color: '#a78bfa',
  },
  {
    step: '03',
    title: 'Performance Attribution',
    desc: 'Results are broken down by long/short, by year, and by market regime. Every metric is calculated independently and cross-verified.',
    color: '#38bdf8',
  },
];

const HowItWorks = () => {
  const [ref, vis] = useVisible(0.1);
  return (
    <div ref={ref} style={{
      marginTop: 52,
      opacity: vis ? 1 : 0, transform: vis ? 'none' : 'translateY(20px)',
      transition: 'opacity 0.7s 0.05s ease, transform 0.7s 0.05s ease',
    }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ color: '#38FE60', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600 }}>
            Process
          </span>
        </div>
        <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 800, letterSpacing: '-0.04em', marginBottom: 6 }}>
          How Backtesting Works
        </h2>
        <p style={{ color: '#555', fontSize: 13, maxWidth: 500, lineHeight: 1.75 }}>
          A rigorous three-stage process ensures results reflect what would have actually happened in live trading.
        </p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
        {HOW_STEPS.map((s, i) => (
          <StepCard key={s.step} step={s} delay={i * 80} />
        ))}
      </div>
    </div>
  );
};

const StepCard = ({ step: s, delay }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: '28px 28px',
        borderRadius: CARD.radius,
        background: CARD.bg,
        border: hov ? CARD.borderHov : CARD.border,
        boxShadow: hov ? '0 16px 48px rgba(0,0,0,0.3)' : 'none',
        transform: hov ? 'translateY(-2px)' : 'none',
        transition: 'all 0.25s ease',
        cursor: 'default',
        position: 'relative',
        overflow: 'hidden',
      }}>
            <span style={{
        position: 'absolute', top: 16, right: 20,
        fontSize: 48, fontWeight: 900, color: 'rgba(255,255,255,0.03)',
        letterSpacing: '-0.04em', lineHeight: 1, userSelect: 'none',
      }}>{s.step}</span>
      <div style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        padding: '4px 10px', borderRadius: 8, marginBottom: 18,
        background: `rgba(${s.color === '#38FE60' ? '56,254,96' : s.color === '#a78bfa' ? '167,139,250' : '56,189,248'},0.1)`,
        border: `1px solid rgba(${s.color === '#38FE60' ? '56,254,96' : s.color === '#a78bfa' ? '167,139,250' : '56,189,248'},0.2)`,
      }}>
        <span style={{ color: s.color, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em' }}>STEP {s.step}</span>
      </div>
      <p style={{ color: '#e0e0e0', fontSize: 14, fontWeight: 700, marginBottom: 10, letterSpacing: '-0.02em' }}>{s.title}</p>
      <p style={{ color: '#555', fontSize: 12, lineHeight: 1.75 }}>{s.desc}</p>
    </div>
  );
};

const ASSET_TAGS = [
  { tag: 'BTC/USDT',  hl: true  }, { tag: 'ETH/USDT',   hl: true  },
  { tag: 'SOL/USDT',  hl: true  }, { tag: 'BNB/USDT',   hl: true  },
  { tag: 'XRP/USDT',  hl: true  }, { tag: 'ADA/USDT',   hl: true  },
  { tag: 'AVAX/USDT', hl: true  }, { tag: 'DOGE/USDT',  hl: false },
  { tag: 'MATIC/USDT',hl: false }, { tag: 'DOT/USDT',   hl: false },
  { tag: 'LINK/USDT', hl: false }, { tag: 'ATOM/USDT',  hl: false },
  { tag: 'UNI/USDT',  hl: false }, { tag: 'LTC/USDT',   hl: false },
  { tag: 'FIL/USDT',  hl: false }, { tag: 'INJ/USDT',   hl: false },
  { tag: 'APT/USDT',  hl: false }, { tag: 'ARB/USDT',   hl: false },
];

const CryptoAssets = () => {
  const [ref, vis] = useVisible(0.1);
  return (
    <div ref={ref} style={{
      marginTop: 52, marginBottom: 16,
      opacity: vis ? 1 : 0, transform: vis ? 'none' : 'translateY(20px)',
      transition: 'opacity 0.7s 0.05s ease, transform 0.7s 0.05s ease',
    }}>
      <div style={{
        borderRadius: 28,
        overflow: 'hidden',
        border: CARD.border,
        background: CARD.bg,
        padding: '44px 40px',
        position: 'relative',
      }}>
                <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'radial-gradient(ellipse at 80% 50%, rgba(56,254,96,0.05) 0%, transparent 60%)',
        }} />
        <div style={{ position: 'relative', display: 'flex', flexWrap: 'wrap', gap: 40, alignItems: 'center' }}>
                    <div style={{ flex: '0 0 auto', maxWidth: 380 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <span style={{ color: '#38FE60', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600 }}>
                Coverage
              </span>
            </div>
            <h2 style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)', fontWeight: 800, letterSpacing: '-0.04em', marginBottom: 12 }}>
              Test Across<br />
              <span style={{ color: '#38FE60', textShadow: '0 0 32px rgba(56,254,96,0.3)' }}>1,000+ Crypto</span><br />
              Assets
            </h2>
            <p style={{ color: '#555', fontSize: 13, lineHeight: 1.75, marginBottom: 24 }}>
              The strategy engine supports all major perpetual and spot pairs across Binance, Bybit, OKX, and more. Validate performance on any asset before deploying capital.
            </p>
            <div style={{ display: 'flex', gap: 24 }}>
              {[['1,000+', 'Trading pairs'], ['3yr+', 'History'], ['24/7', 'Coverage']].map(([v, l]) => (
                <div key={l}>
                  <p style={{ color: '#fff', fontSize: 18, fontWeight: 800, letterSpacing: '-0.03em' }}>{v}</p>
                  <p style={{ color: '#444', fontSize: 11, marginTop: 3 }}>{l}</p>
                </div>
              ))}
            </div>
          </div>
                    <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {ASSET_TAGS.map(({ tag, hl }) => (
                <span key={tag} style={{
                  padding: '6px 12px', borderRadius: 8,
                  background: hl ? 'rgba(56,254,96,0.08)' : 'rgba(255,255,255,0.04)',
                  border: hl ? '1px solid rgba(56,254,96,0.18)' : '1px solid rgba(255,255,255,0.07)',
                  color: hl ? '#38FE60' : '#555',
                  fontSize: 11, fontFamily: 'monospace', fontWeight: 600,
                  transition: 'all 0.2s ease',
                }}>
                  {tag}
                </span>
              ))}
              <span style={{
                padding: '6px 12px', borderRadius: 8,
                background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)',
                color: '#333', fontSize: 11, fontFamily: 'monospace',
              }}>
                +982 more
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TABS = [
  { id: 'trades', label: 'Trade Statistics' },
  { id: 'audit',  label: 'Audit'            },
];

const Backtesting = () => {
  const [tab, setTab] = useState('trades');

  return (
    <div style={{
      minHeight: '100vh',
      background: '#111113',
      color: '#fff',
      paddingTop: 96,
      paddingBottom: 96,
      fontFamily: "'Space Grotesk', sans-serif",
    }}>
      <RiskWarningModal />
      <RiskDisclaimerBanner />

      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '0 24px' }}>

                <div style={{ marginBottom: 40, marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <span style={{
              display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
              background: '#38FE60', boxShadow: '0 0 10px #38FE60',
            }} className="animate-pulse" />
            <span style={{ color: '#38FE60', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600 }}>
              Live Strategy
            </span>
          </div>
          <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 800, letterSpacing: '-0.045em', marginBottom: 12 }}>
            Strategy Backtesting
          </h1>
          <p style={{ color: '#555', fontSize: 14, maxWidth: 500, lineHeight: 1.75 }}>
            779 trades across 25 months. Transparent, independently verifiable performance data.
          </p>
        </div>

                <HeroCard />

                <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
          gap: 10, marginBottom: 10,
        }}>
          <MetricCard label="Win Rate"      value="56.48%"  color="#38FE60" sub="440 of 779 trades"   delay={0}   />
          <MetricCard label="Max Drawdown"  value="−24.6%"  color="#ff6b63" sub="Peak-to-trough"       delay={50}  />
          <MetricCard label="Profit Factor" value="1.84×"   color="#a78bfa" sub="Gross profit / loss"  delay={100} />
          <MetricCard label="Sharpe Ratio"  value="2.14"    color="#38bdf8" sub="Risk-adjusted return"  delay={150} />
        </div>

                <MonthlyGrid />

                <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 12,
          padding: '12px 18px', borderRadius: 14, marginBottom: 20,
          background: 'rgba(255,255,255,0.015)',
          border: '1px solid rgba(255,255,255,0.05)',
        }}>
          <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#444', flexShrink: 0, marginTop: 5 }} />
          <p style={{ color: '#3e3e3e', fontSize: 11, lineHeight: 1.65 }}>
            <span style={{ color: '#555' }}>Disclaimer — </span>
            Simulated backtest results using historical data only. Past performance does not guarantee future results. Real trading involves additional costs, slippage, and market risks.
          </p>
        </div>

                <div style={{
          display: 'flex', gap: 4, padding: 4,
          borderRadius: 16, marginBottom: 12,
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.06)',
          width: 'fit-content',
        }}>
          {TABS.map(t => {
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                padding: '8px 22px', borderRadius: 12,
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: 'none', fontFamily: "'Space Grotesk', sans-serif",
                background: active ? '#38FE60' : 'transparent',
                color: active ? '#000' : '#666',
                boxShadow: active ? '0 0 18px rgba(56,254,96,0.3)' : 'none',
                transition: 'all 0.2s ease',
                letterSpacing: '-0.01em',
              }}>
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'trades' && <TradeStats />}
        {tab === 'audit'  && <AuditTable />}

                <AIAnalysis />

                <TradesPerDay />

                <BacktestingFeatures />

                <HowItWorks />

                <CryptoAssets />

      </div>
    </div>
  );
};

export default Backtesting;
